import { useEffect } from 'react';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import WhatsAppFloat from '../../components/feature/WhatsAppFloat';
import Button from '../../components/base/Button';

const About = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-white font-['Poppins']">
      <Header />
      
      {/* Hero Section */}
      <section className="pt-24 pb-16 bg-gradient-to-r from-red-600 to-black text-white">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-4xl lg:text-6xl font-bold mb-6">About Phoenix Achievers Team</h1>
            <p className="text-xl lg:text-2xl opacity-90 leading-relaxed">
              Empowering entrepreneurs and nonprofit leaders to build, grow, and sustain their organizations through strategic consulting and support services.
            </p>
          </div>
        </div>
      </section>

      {/* Founder Section */}
      <section className="py-20">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="max-w-6xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div className="order-2 lg:order-1">
                <h2 className="text-3xl lg:text-4xl font-bold text-black mb-6">
                  Meet Our Founder
                </h2>
                <h3 className="text-2xl font-semibold text-red-600 mb-4">
                  Abiodun Oluwasesan
                </h3>
                <div className="space-y-6 text-gray-700 leading-relaxed">
                  <p className="text-lg">
                    Educated in both Nigeria and the USA, Abiodun Oluwasesan brings a unique global perspective to business consulting. With extensive experience in international business practices and regulations, he has successfully guided Phoenix Achievers Team to serve over 300+ clients worldwide.
                  </p>
                  <p className="text-lg">
                    His expertise spans across multiple industries and jurisdictions, making him uniquely qualified to help businesses navigate the complex landscape of international business formation, government contracting, and regulatory compliance.
                  </p>
                  <p className="text-lg">
                    Under his leadership, Phoenix Achievers Team has built a reputation for professionalism, transparency, and delivering fast results that exceed client expectations.
                  </p>
                </div>
                <div className="mt-8">
                  <Button 
                    onClick={() => window.open('https://www.fiverr.com/users/abiodun136', '_blank')}
                    className="bg-red-600 hover:bg-red-700 text-white px-8 py-4 text-lg whitespace-nowrap"
                  >
                    Book Consultation with Abiodun
                  </Button>
                </div>
              </div>
              <div className="order-1 lg:order-2">
                <div className="relative">
                  <div className="bg-gradient-to-br from-red-600 to-black rounded-2xl p-8 transform rotate-3">
                    <div className="bg-white rounded-xl p-4 transform -rotate-3">
                      <img 
                        src="https://static.readdy.ai/image/73ef2aa8b67f0b1ba51e6666fcef6024/670bb5513555c22344a70d4af32f9a61.png"
                        alt="Abiodun Oluwasesan - Founder of Phoenix Achievers Team LLC"
                        className="w-full h-96 object-cover object-top rounded-lg"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Core Values Section */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-3xl lg:text-4xl font-bold text-black mb-6">Our Core Values</h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                These fundamental principles guide everything we do and shape how we serve our clients worldwide.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {/* Trust */}
              <div className="bg-white rounded-xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 text-center group">
                <div className="w-16 h-16 bg-red-600 rounded-full flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform">
                  <i className="ri-shield-check-line text-2xl text-white"></i>
                </div>
                <h3 className="text-2xl font-bold text-black mb-4">Trust</h3>
                <p className="text-gray-600 leading-relaxed">
                  We build lasting relationships through transparency, reliability, and ethical business practices. Our clients trust us because we deliver on our promises consistently.
                </p>
              </div>

              {/* Strategy */}
              <div className="bg-white rounded-xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 text-center group">
                <div className="w-16 h-16 bg-red-600 rounded-full flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform">
                  <i className="ri-lightbulb-line text-2xl text-white"></i>
                </div>
                <h3 className="text-2xl font-bold text-black mb-4">Strategy</h3>
                <p className="text-gray-600 leading-relaxed">
                  Every solution is tailored to your specific needs and goals. We develop strategic approaches that maximize your success and minimize risks in your business journey.
                </p>
              </div>

              {/* Growth */}
              <div className="bg-white rounded-xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 text-center group">
                <div className="w-16 h-16 bg-red-600 rounded-full flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform">
                  <i className="ri-line-chart-line text-2xl text-white"></i>
                </div>
                <h3 className="text-2xl font-bold text-black mb-4">Growth</h3>
                <p className="text-gray-600 leading-relaxed">
                  We're committed to your long-term success. Our services are designed to not just solve immediate challenges but to position you for sustainable growth and expansion.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Global Reach Section */}
      <section className="py-20">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-3xl lg:text-4xl font-bold text-black mb-6">Global Reach, Local Expertise</h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                We serve clients across multiple countries, bringing local expertise and global perspective to every project.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <div className="bg-gradient-to-br from-red-600 to-red-700 text-white rounded-xl p-6 text-center">
                <div className="text-4xl mb-3">🇺🇸</div>
                <h3 className="text-xl font-bold mb-2">United States</h3>
                <p className="text-red-100">Business formation, government contracts, and compliance services</p>
              </div>
              
              <div className="bg-gradient-to-br from-black to-gray-800 text-white rounded-xl p-6 text-center">
                <div className="text-4xl mb-3">🇬🇧</div>
                <h3 className="text-xl font-bold mb-2">United Kingdom</h3>
                <p className="text-gray-300">Company registration, VAT services, and business development</p>
              </div>
              
              <div className="bg-gradient-to-br from-red-600 to-black text-white rounded-xl p-6 text-center">
                <div className="text-4xl mb-3">🇳🇬</div>
                <h3 className="text-xl font-bold mb-2">Nigeria</h3>
                <p className="text-red-100">Local business setup, CAC registration, and regulatory compliance</p>
              </div>
              
              <div className="bg-gradient-to-br from-gray-700 to-black text-white rounded-xl p-6 text-center">
                <div className="text-4xl mb-3">🌍</div>
                <h3 className="text-xl font-bold mb-2">Global</h3>
                <p className="text-gray-300">International expansion and cross-border business solutions</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Team Image Section */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl lg:text-4xl font-bold text-black mb-8">Our Dedicated Team</h2>
            <div className="bg-white rounded-2xl p-8 shadow-lg">
              <img 
                src="https://public.readdy.ai/ai/img_res/5dbe5384e9108897de1a30abe390d58f.jpg"
                alt="Phoenix Achievers Team LLC - Professional Consulting Team"
                className="w-full h-64 object-cover object-center rounded-xl mb-6"
              />
              <p className="text-lg text-gray-600 leading-relaxed">
                Our experienced team of consultants, strategists, and specialists work together to provide comprehensive solutions 
                for your business needs. With expertise across multiple industries and jurisdictions, we're equipped to handle 
                projects of any complexity.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-red-600 to-black text-white">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl lg:text-4xl font-bold mb-6">Ready to Achieve Your Goals?</h2>
            <p className="text-xl mb-8 opacity-90">
              Join over 300+ satisfied clients who have trusted Phoenix Achievers Team with their business success.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                onClick={() => window.open('https://www.upwork.com/agencies/1960314973182392391/', '_blank')}
                className="bg-red-600 text-white hover:bg-red-700 px-8 py-4 text-lg font-semibold whitespace-nowrap"
              >
                Book Consultation
              </Button>
              <Button 
                onClick={() => window.open('https://wa.me/14697380696', '_blank')}
                className="bg-transparent border-2 border-white text-white hover:bg-white hover:text-red-600 px-8 py-4 text-lg font-semibold whitespace-nowrap"
              >
                <i className="ri-whatsapp-line mr-2"></i>
                DM Us for Help 🚀
              </Button>
            </div>
          </div>
        </div>
      </section>

      <Footer />
      <WhatsAppFloat />
    </div>
  );
};

export default About;